import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/config";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Package, Lock, User } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Login() {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Static modern colors
  const BRAND_BLUE = "#3b82f6";
  const BRAND_PURPLE = "#8b5cf6";
  const navigate = useNavigate();

  // Design settings removed - using static colors

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get current language from i18n (already set by LanguageSwitcher)
      const currentLanguage = i18n.language;
      
      const response = await axiosInstance.post("/auth/login", {
        username,
        password,
      });

      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      // Save and apply the language
      localStorage.setItem("language", currentLanguage);
      
      // Save selected language to backend for future logins
      try {
        await axiosInstance.put("/auth/language", null, {
          params: { language: currentLanguage }
        });
        console.log(`Language saved to backend: ${currentLanguage}`);
      } catch (err) {
        console.error("Error saving language:", err);
      }
      
      toast.success(t('auth.loginSuccess'));
      
      // Use window.location to ensure full page reload with new token
      // This ensures axios interceptor picks up the token
      window.location.href = "/dashboard";
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-100" data-testid="login-page">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: `${BRAND_PURPLE}15` }}></div>
        <div className="absolute -bottom-1/2 -right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: `${BRAND_BLUE}15` }}></div>
      </div>

      <Card className="w-full max-w-md relative z-10 bg-white shadow-2xl border-0 rounded-2xl">
        <CardHeader className="space-y-4 text-center pt-8">
          <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: `linear-gradient(135deg, ${BRAND_PURPLE}, ${BRAND_BLUE})` }}>
            <Package className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">{t('auth.title')}</CardTitle>
          <CardDescription className="text-base text-gray-600">
            {t('auth.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Language Switcher - prominently placed at the top */}
          <div className="mb-6 pb-5 border-b border-gray-200">
            <div className="flex justify-center">
              <LanguageSwitcher variant="card" />
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                {t('auth.username')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="username"
                  type="text"
                  placeholder={t('auth.enterUsername')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="pl-10 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="login-username-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder={t('auth.enterPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-white font-medium text-base shadow-lg hover:shadow-xl transition-all duration-200 hover:opacity-90"
              style={{ background: `linear-gradient(to right, ${BRAND_PURPLE}, ${BRAND_BLUE})` }}
              data-testid="login-submit-button"
            >
              {loading ? t('common.loading') : t('common.login')}
            </Button>
          </form>
        </CardContent>
      </Card>
      
      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 pb-6 text-center text-sm text-gray-600 space-y-2">
        <p>© 2025 - Kauz Logistik e.K. - Industriestr. 18 - DE-63834 Sulzbach</p>
        <p><a href="mailto:mbe0020@mbe.de" className="text-blue-600 hover:underline">mbe0020@mbe.de</a></p>
      </div>
    </div>
  );
}
